import { LEGACY_USDV_MINT, USDV_MINT } from "@shared/tokens/usdv";
import { Base58Util } from "@solomon-labs/base58";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { CacheDuration } from "@solomon-labs/caching";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import type { SolanaTransactionSignature } from "@solomon-labs/types";

import type { MigrationAttemptRecord } from "./analytics";

export interface PendingMigrationSwap {
    analytics?: MigrationAttemptRecord;
    leg: "legacy-to-usdc" | "usdc-to-usdv";
    signature: string;
    recentBlockhash: string;
    /** Present when Jupiter must still add the primary (fee payer) signature. */
    searchFromSlot?: number;
    /** Exact input, in raw token units. Both migration legs use ExactIn. */
    inputAmount: string;
    /** Expected legacy balance after this swap; protects against stale reads after reload. */
    remainingLegacyUsdv: string;
}

export type MigrationSwapResolution =
    | { status: "pending" }
    | { status: "failed" }
    | { status: "confirmed"; usdcDelta: BigDecimal };

type TransactionMeta = NonNullable<NonNullable<Awaited<ReturnType<typeof ConnectionManager.getTransaction>>>["meta"]>;

interface ShortVector {
    value: number;
    nextOffset: number;
}

function readShortVector(bytes: Uint8Array, startOffset: number): ShortVector {
    let value = 0;
    let shift = 0;
    let offset = startOffset;
    while (offset < bytes.length && shift <= 14) {
        const byte = bytes[offset++];
        value |= (byte & 0x7f) << shift;
        if ((byte & 0x80) === 0) return { value, nextOffset: offset };
        shift += 7;
    }
    throw new Error("Invalid Solana short vector.");
}

function readRecoveryIdentity(bytes: Uint8Array): {
    primarySignature: string | null;
    signature: string | null;
    recentBlockhash: string;
} {
    const signatureCount = readShortVector(bytes, 0);
    const signaturesEnd = signatureCount.nextOffset + signatureCount.value * 64;
    if (signaturesEnd >= bytes.length) throw new Error("Invalid signed Solana transaction.");

    const signatures = Array.from({ length: signatureCount.value }, (_, index) => {
        const start = signatureCount.nextOffset + index * 64;
        const value = bytes.slice(start, start + 64);
        return value.some((byte) => byte !== 0) ? Base58Util.encode(value) : null;
    });

    let messageOffset = signaturesEnd;
    const versionPrefix = bytes[messageOffset];
    if ((versionPrefix & 0x80) !== 0) {
        if ((versionPrefix & 0x7f) !== 0) throw new Error("Unsupported Solana transaction version.");
        messageOffset += 1;
    }
    const accountCount = readShortVector(bytes, messageOffset + 3);
    const blockhashOffset = accountCount.nextOffset + accountCount.value * 32;
    if (blockhashOffset + 32 > bytes.length) throw new Error("Invalid signed Solana transaction.");

    return {
        primarySignature: signatures[0] ?? null,
        signature: signatures.find((value) => value !== null) ?? null,
        recentBlockhash: Base58Util.encode(bytes.slice(blockhashOffset, blockhashOffset + 32))
    };
}

export async function createPendingMigrationSwap(
    bytes: Uint8Array,
    leg: PendingMigrationSwap["leg"],
    inputAmount: string,
    remainingLegacyUsdv: string
): Promise<PendingMigrationSwap> {
    // Recovery only needs wire-level signatures and the blockhash. Resolving
    // Jupiter's address lookup tables here would require extra RPC reads and
    // fails before a valid signed transaction can be submitted.
    const { primarySignature, signature, recentBlockhash } = readRecoveryIdentity(bytes);
    // Without a transaction ID there is no safe way to recover an ambiguous submission.
    if (!signature || !recentBlockhash) {
        throw new Error("This swap could not be prepared for recovery. Request a new quote and try again.");
    }
    const pending = { leg, signature, recentBlockhash, inputAmount, remainingLegacyUsdv };
    if (primarySignature === signature) return pending;
    // A secondary signature still identifies the signed message after Jupiter co-signs it.
    const searchFromSlot = await ConnectionManager.getRpcClient().request<number>("getSlot", [
        { commitment: "finalized" }
    ]);
    if (!Number.isSafeInteger(searchFromSlot) || searchFromSlot < 0)
        throw new Error("Could not prepare swap recovery. Try again.");
    return { ...pending, searchFromSlot };
}

function rawDelta(meta: TransactionMeta, owner: string, mint: string): BigDecimal {
    if (!meta.preTokenBalances || !meta.postTokenBalances) {
        throw new Error("The swap receipt is incomplete. Check its status again.");
    }
    const total = (balances: NonNullable<TransactionMeta["postTokenBalances"]>): BigDecimal =>
        balances.reduce(
            (sum, balance) =>
                balance.owner === owner && balance.mint === mint
                    ? sum.add(new BigDecimal(balance.uiTokenAmount.amount))
                    : sum,
            new BigDecimal(0)
        );
    return total(meta.postTokenBalances).subtract(total(meta.preTokenBalances));
}

async function fetchTransaction(
    signature: string
): Promise<Awaited<ReturnType<typeof ConnectionManager.getTransaction>>> {
    return ConnectionManager.getTransaction(
        signature as SolanaTransactionSignature,
        { commitment: "confirmed", maxSupportedTransactionVersion: 0 },
        { cacheDuration: CacheDuration.Bust }
    );
}

/** Read-only recovery: never sign or broadcast a replacement for an uncertain swap. */
function resolveMeta(owner: string, pending: PendingMigrationSwap, meta: TransactionMeta): MigrationSwapResolution {
    if (meta.err) return { status: "failed" };
    const inputMint = pending.leg === "legacy-to-usdc" ? LEGACY_USDV_MINT : WELL_KNOWN_TOKENS_STRINGS.USDC;
    const outputMint = pending.leg === "legacy-to-usdc" ? WELL_KNOWN_TOKENS_STRINGS.USDC : USDV_MINT;
    if (
        !rawDelta(meta, owner, inputMint).eq(new BigDecimal(pending.inputAmount).multiply(-1)) ||
        !rawDelta(meta, owner, outputMint).gt(0)
    ) {
        throw new Error("The swap receipt does not match this migration. Check the transaction before continuing.");
    }
    return {
        status: "confirmed",
        usdcDelta: rawDelta(meta, owner, WELL_KNOWN_TOKENS_STRINGS.USDC).divide(BigDecimal.pow10(6))
    };
}

/** Locate a co-signed transaction by its original wallet signature, never by balance guesses. */
async function recoverCosignedSwap(
    owner: string,
    pending: PendingMigrationSwap,
    finalizedSlot: number
): Promise<MigrationSwapResolution> {
    const searchFromSlot = pending.searchFromSlot;
    if (searchFromSlot === undefined) return { status: "pending" };
    let before: string | undefined;
    // Bound expensive history reads. Incomplete history always keeps the swap locked.
    for (let page = 0; page < 10; page += 1) {
        const history = await ConnectionManager.getRpcClient().request<Array<{ signature: string; slot: number }>>(
            "getSignaturesForAddress",
            [
                owner,
                { commitment: "finalized", minContextSlot: finalizedSlot, limit: 100, ...(before ? { before } : {}) }
            ]
        );
        for (const entry of history) {
            if (entry.slot < searchFromSlot) return { status: "failed" };
            const transaction = await fetchTransaction(entry.signature);
            if (!transaction?.meta) return { status: "pending" };
            const body = transaction.transaction;
            if (typeof body !== "object" || !("signatures" in body)) return { status: "pending" };
            if (body.signatures.includes(pending.signature as SolanaTransactionSignature)) {
                return resolveMeta(owner, pending, transaction.meta);
            }
        }
        if (history.length < 100) return { status: "failed" };
        before = history[history.length - 1]?.signature;
    }
    return { status: "pending" };
}

export async function recoverMigrationSwap(
    owner: string,
    pending: PendingMigrationSwap
): Promise<MigrationSwapResolution> {
    if (pending.searchFromSlot === undefined) {
        const meta = (await fetchTransaction(pending.signature))?.meta;
        if (meta) return resolveMeta(owner, pending, meta);
    }

    const rpc = ConnectionManager.getRpcClient();
    const validity = await rpc.request<{ context: { slot: number }; value: boolean }>("isBlockhashValid", [
        pending.recentBlockhash,
        { commitment: "finalized" }
    ]);
    if (validity.value !== false || !Number.isSafeInteger(validity.context.slot)) return { status: "pending" };
    if (pending.searchFromSlot !== undefined) return recoverCosignedSwap(owner, pending, validity.context.slot);
    // Query history AFTER observing finalized expiry. An expired hash alone does not prove the swap failed.
    const statuses = await rpc.request<{
        context: { slot: number };
        value: Array<{ err: unknown; confirmationStatus?: string | null } | null>;
    }>("getSignatureStatuses", [[pending.signature], { searchTransactionHistory: true }]);
    if (
        !Number.isSafeInteger(statuses.context.slot) ||
        statuses.context.slot < validity.context.slot ||
        statuses.value.length !== 1
    )
        return { status: "pending" };
    const status = statuses.value[0];
    if (status === null || (status?.err && status.confirmationStatus === "finalized")) return { status: "failed" };
    return { status: "pending" };
}

/** Read old session records written before both swap legs had durable transaction identities. */
export async function recoverIntermediateUsdc(owner: string, signature: string): Promise<BigDecimal> {
    const meta = (await fetchTransaction(signature))?.meta;
    if (!meta || meta.err) throw new Error("The swap receipt is not available yet. Retry the balance check.");
    const received = rawDelta(meta, owner, WELL_KNOWN_TOKENS_STRINGS.USDC);
    if (!received.gt(0)) throw new Error("The swap receipt has no received USDC for this wallet.");
    return received.divide(BigDecimal.pow10(6));
}

export function confirmedUsdcAmount(receipt: {
    totalOutputAmount?: string;
    outputAmountResult?: string;
}): BigDecimal | null {
    const raw = receipt.totalOutputAmount ?? receipt.outputAmountResult;
    return raw && /^\d+$/.test(raw) && BigInt(raw) > 0n ? new BigDecimal(raw).divide(BigDecimal.pow10(6)) : null;
}
